import { Injectable } from '@angular/core';

import { Moment } from 'moment';
import { AnnualPlanItemRightsList } from './annual-plan-rights';
import { ApiBaseService } from "../../common/providers/api-base-service";
import { AnnualPlanData, AnnualPlanDataMerger, AnnualPlanEffectiveData } from '../merger/annual-plan-data-merger';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UrlFormatter } from '../../common/http/url-formatter';
import { User } from "../../common/models/user";
import { Organization } from "../../common/models/organization";
import { AnnualPlan } from "../../common/models/annual-plan";
import { Library } from "../../common/models/library";
import { Athlete } from "../../common/models/athlete";
import { CurrentUser } from "../../common/providers/current-user";

@Injectable({providedIn: 'root'})
export class AnnualPlanService {
    constructor(
        private api: ApiBaseService,
        private dataMerger: AnnualPlanDataMerger,
        private url: UrlFormatter,
        private currentUser: CurrentUser,
    ) {}

    public remove(organization: Organization, plan: AnnualPlan): Observable<void> {
        return this.api.call('delete',`organizations/${organization.code}/plans/${plan.id}`, {}, this.currentUser.getAccessToken());
    }

    public edit(plan :any, langs: number[] = []): Observable<AnnualPlan> {
        plan.langs = langs;

        return this.api.call('put', `organizations/${plan.organization.code}/plans/${plan.id}/edit`, plan, this.currentUser.getAccessToken()).pipe(
            map((p) => AnnualPlan.fromAPI(p))
        );
    }

    public new(plan :any, langs: number[] = []): Observable<AnnualPlan> {
        plan.langs = langs;

        return this.api.call('post',`organizations/${plan.organization.code}/plans/new`, plan, this.currentUser.getAccessToken()).pipe(
            map((p) => AnnualPlan.fromAPI(p))
        );
    }

    /**
     * Create or edit an annual plan
     *
     * @param plan
     */
    public save(plan: any, langs: number[] = []): Observable<AnnualPlan> {
        if (null != plan.id) {
            return this.edit(plan, langs);
        }

        return this.new(plan, langs);
    }

    public duplicate(plan: any, langs: number[] = []): Observable<AnnualPlan> {
        plan.langs = langs;

        return this.api.call('post',`organizations/${plan.organization.code}/plans/${plan.id}/duplicate`, plan, this.currentUser.getAccessToken()).pipe(
            map((p) => AnnualPlan.fromAPI(p))
        );
    }

    public export(organization :any, plan :any, athletes :any, exportFrom :any, exportTo :any, importStartDate :any): Observable<void> {
        return this.api.call('post',`organizations/${organization.code}/plans/${plan.id}/export`, {
            athletes,
            exportFrom,
            exportTo,
            importStartDate,
        }, this.currentUser.getAccessToken());
    }

    public saveWeeks(organization:any, plan:any, weeks:any): Observable<void> {
        return this.api.call('post',`organizations/${organization.code}/plans/${plan.id}/weeks`, {
            plan,
            weeks
        }, this.currentUser.getAccessToken());
    }

    public savePhases(organization:any, plan:any, phases:any, ignoreOverlap: boolean = false): Observable<any> {
        return this.api.call('post',`organizations/${organization.code}/plans/${plan.id}/phases`, {
            plan,
            phases,
            ignoreOverlap
        }, this.currentUser.getAccessToken());
    }

    public deletePhases(organization:any, plan:any, phaseId:any): Observable<void> {
        return this.api.call('delete',`organizations/${organization.code}/plans/${plan.id}/phases/${phaseId}`, {}, this.currentUser.getAccessToken());
    }

    public saveMacroCycles(organization:any, plan:any, macroCycles:any, ignoreOverlap: boolean = false): Observable<any> {
        return this.api.call('post',`organizations/${organization.code}/plans/${plan.id}/macrocycles`, {
            plan,
            macroCycles,
            ignoreOverlap
        }, this.currentUser.getAccessToken());
    }

    public deleteMacroCycles(organization:any, plan:any, macroCycleId:any): Observable<void> {
        return this.api.call('delete',`organizations/${organization.code}/plans/${plan.id}/macrocycles/${macroCycleId}`, {}, this.currentUser.getAccessToken());
    }

    public findAllByLibrary(organization: Organization, library: Library, filter?: string | null): Observable<AnnualPlan[]> {
        return this.api.call('get',`organizations/${organization.code}/libraries/${library.id}/plans`, {
            ...(filter && { ["filter-key"]: filter })
        }, this.currentUser.getAccessToken()).pipe(
            map((plans) => plans.map((p:any) => AnnualPlan.fromAPI(p)))
        );
    }

    public findAllByAthlete(organization: Organization, athlete: Athlete): Observable<AnnualPlan[]> {
        return this.api.call('get',`organizations/${organization.code}/plans/athletes/${athlete.userId}`, {}, this.currentUser.getAccessToken()).pipe(
            map((plans) => plans.map((p:any) => Object.assign(AnnualPlan.fromAPI(p), {
                startedAtFirstWeekDay: p.startedAtFirstWeekDay,
                endedAtLastWeekDay: p.endedAtLastWeekDay,
                creator: User.fromAPI(p)
            })))
        );
    }

    public findAllByOrganization(organization: Organization): Observable<AnnualPlan[]> {
        return this.api.call('get',`organizations/${organization.code}/plans`, {}, this.currentUser.getAccessToken()).pipe(
            map((plans) => plans.map((p:any) => Object.assign(AnnualPlan.fromAPI(p), {creator: User.fromAPI(p)})))
        );
    }

    public findAllByOrganizationWithoutPlansInMap(organization: Organization, annualPlans: AnnualPlan[]): Observable<AnnualPlan[]> {
        let toRemove = '';
        toRemove += annualPlans.map((plan) => `r[]=${plan.id}`);

        return this.api.call('get',`organizations/${organization.code}/plans?${toRemove}`, {}, this.currentUser.getAccessToken()).pipe(
            map((plans) => plans.map((p:any) => Object.assign(AnnualPlan.fromAPI(p), {creator: User.fromAPI(p)})))
        );
    }

    public findActiveByAthleteUserId(organization: Organization , athleteUserId: number, planOnly = false): Observable<AnnualPlanData> {
        this.url.query.clear();

        if (planOnly) {
            this.url.query.add('ap', true);
        }

        return this.api.call('get',`organizations/${organization.id}/athletes/${athleteUserId}/plans/active`, {}, this.currentUser.getAccessToken()).pipe(
            map((data) => this.dataMerger.mergeData(data))
        );
    }

    public findByPlanId(organizationCode: string, athleteId: number, planId: number): Observable<AnnualPlanData> {
        return this.api.call('get',`organizations/${organizationCode}/plans/${planId}`, {}, this.currentUser.getAccessToken()).pipe(
            map((data) => this.dataMerger.mergeData(data))
        );
    }

    public findEffectiveByPlanId(organizationCode: string, athleteId: number, planId: number): Observable<AnnualPlanEffectiveData> {
        return this.api.call('get',`organizations/${organizationCode}/plans/${planId}/effective`, {}, this.currentUser.getAccessToken()).pipe(
            map((data) => this.dataMerger.mergeEffectiveData(data))
        );
    }

    public findAllByAthleteWithoutPlansInMap(organization: Organization, athlete: Athlete, annualPlans: AnnualPlan[]): Observable<AnnualPlan[]> {
        let toRemove = '';
        toRemove += annualPlans.map((plan) => `r[]=${plan.id}`);

        return this.api.call('get',`organizations/${organization.code}/plans/athletes/${athlete.userId}?${toRemove}`, {}, this.currentUser.getAccessToken()).pipe(
            map((plans) => plans.map((p:any) => Object.assign(AnnualPlan.fromAPI(p), {creator: User.fromAPI(p)})))
        );
    }

    public assignPlanToAthlete(organization: Organization, annualPlan: AnnualPlan, athlete: Athlete, startedAt: Moment): Observable<AnnualPlan> {
        return this.api.call('post',`organizations/${organization.id}/plans/${athlete.userId}/add`, {
            annualPlan,
            athlete,
            startedAt,
        }, this.currentUser.getAccessToken()).pipe(
            map(
                (plan) => AnnualPlan.fromAPI(plan)
            )
        );
    }

    public markAsActiveByAnnualPlan(organization: Organization, plan: AnnualPlan): Observable<void> {
        const url = `organizations/${organization.code}/plans/${plan.id}/toggle-status`;
        return this.api.call('post',url, {}, this.currentUser.getAccessToken());
    }
}

export interface AnnualPlanRow {
    id: number;
    title: string;
    athleteId: number;
    libraryId: number;
    weeksLength?: number;
    weeklyWorkingTime?: number;
    comment?: string;
    creator: User;
    rights?: AnnualPlanItemRightsList;
    availableLangIds?: number[];
}
